import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Modal from '../components/Modal';

function SfmcAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showPermissionsHelp, setShowPermissionsHelp] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const [testingAccount, setTestingAccount] = useState(null);
  const [formData, setFormData] = useState({
    accountName: '',
    clientId: '',
    clientSecret: '',
    authBaseUri: '',
    restBaseUri: '',
    soapBaseUri: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/sfmc-accounts');
      setAccounts(response.data);
    } catch (err) {
      setError('Failed to load SFMC accounts');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (account = null) => {
    if (account) {
      setEditingAccount(account);
      setFormData({
        accountName: account.account_name,
        clientId: account.client_id,
        clientSecret: '', // Don't show existing secret
        authBaseUri: account.auth_base_uri,
        restBaseUri: account.rest_base_uri,
        soapBaseUri: account.soap_base_uri
      });
    } else {
      setEditingAccount(null);
      setFormData({
        accountName: '',
        clientId: '',
        clientSecret: '',
        authBaseUri: '',
        restBaseUri: '',
        soapBaseUri: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAccount(null);
    setError('');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingAccount) {
        // Only include clientSecret if it was changed
        const updateData = { ...formData };
        if (!updateData.clientSecret) {
          delete updateData.clientSecret;
        }
        await axios.put(`/api/sfmc-accounts/${editingAccount.id}`, updateData);
      } else {
        await axios.post('/api/sfmc-accounts', formData);
      }
      await fetchAccounts();
      handleCloseModal();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save account');
    }
  };

  const handleTest = async (accountId) => {
    setTestingAccount(accountId);
    try {
      const response = await axios.post(`/api/sfmc-accounts/${accountId}/test`);
      alert(`Connection successful!\n${response.data.message}`);
    } catch (err) {
      alert(`Connection failed: ${err.response?.data?.error || 'Unknown error'}`);
    } finally {
      setTestingAccount(null);
    }
  };

  const handleDelete = async (accountId, accountName) => {
    if (!window.confirm(`Are you sure you want to delete "${accountName}"? This will also delete all associated monitoring configs.`)) {
      return;
    }

    try {
      await axios.delete(`/api/sfmc-accounts/${accountId}`);
      await fetchAccounts();
    } catch (err) {
      alert(`Failed to delete account: ${err.response?.data?.error || 'Unknown error'}`);
    }
  };

  const handleToggleActive = async (accountId, currentStatus) => {
    try {
      await axios.patch(`/api/sfmc-accounts/${accountId}`, {
        isActive: !currentStatus
      });
      await fetchAccounts();
    } catch (err) {
      alert(`Failed to update account: ${err.response?.data?.error || 'Unknown error'}`);
    }
  };

  const PermissionsInfo = () => (
    <div style={{
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h4 style={{ margin: '0 0 8px 0', color: '#1e40af', fontSize: '14px' }}>
            Required API Permissions
          </h4>
          <p style={{ margin: '0 0 12px 0', fontSize: '13px', color: '#1e40af' }}>
            Your Installed Package must have the following permissions enabled:
          </p>
        </div>
        <button
          onClick={() => setShowPermissionsHelp(!showPermissionsHelp)}
          style={{
            background: 'none',
            border: 'none',
            color: '#2563eb',
            cursor: 'pointer',
            fontSize: '12px',
            textDecoration: 'underline'
          }}
        >
          {showPermissionsHelp ? 'Hide details' : 'Show details'}
        </button>
      </div>

      {showPermissionsHelp && (
        <div style={{ marginTop: '12px' }}>
          <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #bfdbfe' }}>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#1e40af' }}>Permission</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#1e40af' }}>Access Level</th>
                <th style={{ textAlign: 'left', padding: '8px 12px', color: '#1e40af' }}>Used For</th>
              </tr>
            </thead>
            <tbody style={{ color: '#1e3a8a' }}>
              <tr style={{ borderBottom: '1px solid #dbeafe' }}>
                <td style={{ padding: '8px 12px' }}>Automations</td>
                <td style={{ padding: '8px 12px' }}>Read</td>
                <td style={{ padding: '8px 12px' }}>Monitor automation status and run history</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dbeafe' }}>
                <td style={{ padding: '8px 12px' }}>Journeys</td>
                <td style={{ padding: '8px 12px' }}>Read</td>
                <td style={{ padding: '8px 12px' }}>Monitor journey status and activity</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dbeafe' }}>
                <td style={{ padding: '8px 12px' }}>Data Extensions</td>
                <td style={{ padding: '8px 12px' }}>Read</td>
                <td style={{ padding: '8px 12px' }}>Check DE row counts and last modified dates</td>
              </tr>
              <tr style={{ borderBottom: '1px solid #dbeafe' }}>
                <td style={{ padding: '8px 12px' }}>Triggered Sends</td>
                <td style={{ padding: '8px 12px' }}>Read</td>
                <td style={{ padding: '8px 12px' }}>Monitor triggered send status</td>
              </tr>
              <tr>
                <td style={{ padding: '8px 12px' }}>Saved Content (optional)</td>
                <td style={{ padding: '8px 12px' }}>Read</td>
                <td style={{ padding: '8px 12px' }}>Browse and explore SFMC objects</td>
              </tr>
            </tbody>
          </table>

          <div style={{ marginTop: '16px', padding: '12px', background: '#dbeafe', borderRadius: '6px' }}>
            <strong style={{ fontSize: '12px', color: '#1e40af' }}>How to set up:</strong>
            <ol style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '12px', color: '#1e3a8a' }}>
              <li>Go to SFMC Setup &gt; Apps &gt; Installed Packages</li>
              <li>Create a new package or edit an existing one</li>
              <li>Add an API Integration component (Server-to-Server)</li>
              <li>Enable the permissions listed above</li>
              <li>Copy the Client ID, Client Secret, and endpoint URLs</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );

  if (loading) {
    return <div className="loading">Loading accounts...</div>;
  }

  return (
    <div>
      <div className="content-header">
        <div>
          <h1 style={{ margin: '0 0 5px 0' }}>SFMC Accounts</h1>
          <p style={{ margin: 0, color: '#6b7280' }}>
            Manage your Salesforce Marketing Cloud connections
          </p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => handleOpenModal()}
        >
          + Add Account
        </button>
      </div>

      <div className="content-body">
        <PermissionsInfo />

        {accounts.length === 0 ? (
          <div className="empty-state">
            <h3>No SFMC Accounts</h3>
            <p>Add your first Salesforce Marketing Cloud account to start monitoring</p>
            <button
              className="btn btn-primary"
              onClick={() => handleOpenModal()}
            >
              + Add Account
            </button>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <table className="table">
                <thead>
                  <tr>
                    <th>Account Name</th>
                    <th>Client ID</th>
                    <th>Auth Base URI</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id}>
                      <td>
                        <strong>{account.account_name}</strong>
                      </td>
                      <td>
                        <code style={{ fontSize: '12px' }}>
                          {account.client_id.substring(0, 20)}...
                        </code>
                      </td>
                      <td>{account.auth_base_uri}</td>
                      <td>
                        <span className={`status-badge ${account.is_active ? 'healthy' : 'unknown'}`}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleTest(account.id)}
                            disabled={testingAccount === account.id}
                          >
                            {testingAccount === account.id ? 'Testing...' : 'Test'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleToggleActive(account.id, account.is_active)}
                          >
                            {account.is_active ? 'Disable' : 'Enable'}
                          </button>
                          <button
                            className="btn btn-secondary"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleOpenModal(account)}
                          >
                            Edit
                          </button>
                          <button
                            className="btn btn-danger"
                            style={{ padding: '6px 12px', fontSize: '12px' }}
                            onClick={() => handleDelete(account.id, account.account_name)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingAccount ? 'Edit SFMC Account' : 'Add SFMC Account'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={handleCloseModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit}>
              {editingAccount ? 'Update' : 'Create'}
            </button>
          </>
        }
      >
        {error && (
          <div style={{
            padding: '12px',
            background: '#fecaca',
            color: '#991b1b',
            borderRadius: '6px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {!editingAccount && (
          <div style={{
            padding: '12px',
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '6px',
            marginBottom: '20px',
            fontSize: '13px',
            color: '#92400e'
          }}>
            <strong>Tip:</strong> Make sure your Installed Package has Read access to Automations, Journeys, Data Extensions, and Triggered Sends.
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Account Name *</label>
            <input
              type="text"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              required
              placeholder="Production SFMC"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              A friendly name to identify this SFMC instance
            </small>
          </div>

          <div className="form-group">
            <label>Client ID *</label>
            <input
              type="text"
              name="clientId"
              value={formData.clientId}
              onChange={handleChange}
              required
              placeholder="xxxxxxxxxxxxxxxxxxxxxxxxxx"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              Found in Setup &gt; Apps &gt; Installed Packages &gt; Your Package
            </small>
          </div>

          <div className="form-group">
            <label>Client Secret {editingAccount ? '(leave blank to keep current)' : '*'}</label>
            <input
              type="password"
              name="clientSecret"
              value={formData.clientSecret}
              onChange={handleChange}
              required={!editingAccount}
              placeholder="Your SFMC Client Secret"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              Only shown once when the package is created - keep it safe!
            </small>
          </div>

          <div className="form-group">
            <label>Auth Base URI *</label>
            <input
              type="url"
              name="authBaseUri"
              value={formData.authBaseUri}
              onChange={handleChange}
              required
              placeholder="https://xxxxxxxxxx.auth.marketingcloudapis.com"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              Authentication endpoint (usually ends with .auth.marketingcloudapis.com)
            </small>
          </div>

          <div className="form-group">
            <label>REST Base URI *</label>
            <input
              type="url"
              name="restBaseUri"
              value={formData.restBaseUri}
              onChange={handleChange}
              required
              placeholder="https://xxxxxxxxxx.rest.marketingcloudapis.com"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              REST API endpoint (usually ends with .rest.marketingcloudapis.com)
            </small>
          </div>

          <div className="form-group">
            <label>SOAP Base URI *</label>
            <input
              type="url"
              name="soapBaseUri"
              value={formData.soapBaseUri}
              onChange={handleChange}
              required
              placeholder="https://xxxxxxxxxx.soap.marketingcloudapis.com"
            />
            <small style={{ color: '#6b7280', fontSize: '11px' }}>
              SOAP API endpoint (usually ends with .soap.marketingcloudapis.com)
            </small>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default SfmcAccounts;
